import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, errorMessage, queries } from "../lib/api";
import { styles } from "../lib/styles";
import { AccessTable } from "../components/AccessTable";
import { ServerForm } from "../components/ServerForm";

export const Route = createFileRoute("/")({ component: ServersPage });
function ServersPage() {
  const me = useQuery(queries.me());
  const state = useQuery({ ...queries.state(), enabled: me.data?.admin === true });
  const cache = useQueryClient();
  const sync = useMutation({
    mutationFn: accessAPI.reconcile,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  if (!me.data?.admin)
    return (
      <section {...stylex.props(styles.panel)}>
        <h1 {...stylex.props(styles.title)}>Your SSH access</h1>
        <p {...stylex.props(styles.description)}>
          Your administrator manages server permissions.{" "}
          <Link to="/connect">Set up your device and view available aliases.</Link>
        </p>
      </section>
    );
  return (
    <>
      <div {...stylex.props(styles.pageHeading)}>
        <div>
          <h1 {...stylex.props(styles.title)}>Servers and access</h1>
          <p {...stylex.props(styles.description)}>
            See which identities a server currently accepts. Group changes become active after
            successful synchronization.
          </p>
        </div>
        <Button
          label="Synchronize access"
          variant="primary"
          isLoading={sync.isPending}
          onClick={() => sync.mutate()}
        />
      </div>
      {sync.isSuccess && (
        <output {...stylex.props(styles.notice)}>
          Synchronization queued. The worker checks for changes every 30 seconds.
        </output>
      )}
      {(state.error || sync.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(state.error || sync.error)}
        </p>
      )}
      {state.data && (
        <>
          <div {...stylex.props(styles.notice)}>
            {state.data.sync_error
              ? `Access issuance is blocked: ${state.data.sync_error}`
              : state.data.last_sync
                ? `Last successful synchronization: ${new Date(state.data.last_sync).toLocaleString()}`
                : "No successful synchronization yet. Certificate issuance remains blocked."}
          </div>
          <section {...stylex.props(styles.panel)}>
            <AccessTable state={state.data} />
          </section>
          {state.data.deployments
            .filter((d) => d.error)
            .map((d) => (
              <p key={d.server_id} {...stylex.props(styles.error)} role="alert">
                {d.server_id}: {d.error}
              </p>
            ))}
        </>
      )}
      <div {...stylex.props(styles.grid)}>
        <ServerForm />
        <aside {...stylex.props(styles.panel)}>
          <h2 {...stylex.props(styles.panelHeading)}>A complete access grant</h2>
          <p {...stylex.props(styles.description)}>
            A designated NetBird group selects the people. An access rule selects the server and
            Linux account. A successful deployment makes that rule effective.
          </p>
          <pre {...stylex.props(styles.code)}>
            Company identity{`\n`} + NetBird SSH group{`\n`} + Applied server rule{`\n`} =
            Short-lived SSH access
          </pre>
          <p {...stylex.props(styles.small)}>
            Certificates last up to eight hours. Existing SSH sessions do not automatically close
            when a certificate expires.
          </p>
        </aside>
      </div>
    </>
  );
}
